/**
 * Cross-table attention cues: dedup, and the countdown floor.
 *
 * Two problems this solves, both created by the same fact — **one table is
 * mounted in more than one place at a time**. Its carousel panel and its grid
 * cell are separate React subtrees for the same gameID, each running its own
 * timers and each holding its own `useSound` handles.
 *
 * ## 1. Dedup — a cue must be heard once, not twice
 *
 * Ambient table sound is silenced per-panel by `TableActiveContext`, so only
 * one subtree ever plays it. The ATTENTION cues (your turn, the countdown,
 * out of time) deliberately opt out of that gate — they have to be audible
 * from a table the player is NOT looking at — which means every mounted copy
 * of that table would fire them. `claimCue` gives the first caller the cue and
 * makes the rest no-ops for a short window.
 *
 * ## 2. The countdown floor — only ONE table beeps at a time
 *
 * The 5·4·3·2·1 countdown is the cue most likely to overlap, and several
 * tables counting down at once is what makes multi-tabling feel chaotic
 * (Marlon + Lynnell raised exactly this in March on Unity, CX-1442; owner call
 * 2026-08-25). Only the table CLOSEST TO ZERO beeps; the rest stay silent
 * until it resolves, then the next-closest takes over mid-count.
 *
 * Priority is by **deadline**, not by turn-start order: a table whose clock was
 * extended must not hold the floor ahead of one about to expire. The table the
 * player is LOOKING AT does not enter the race at all — it always owns its own
 * countdown (EN-10585 AC4, see holdsCountdownFloor), and the deadline race
 * arbitrates the background tables only.
 *
 * Safety property, and the reason this is safe to ship: a claim carries its own
 * expiry, so it stops counting the moment its deadline passes. A table that
 * disconnects, stalls, or unmounts mid-turn cannot permanently silence the
 * others — the worst case self-heals within that table's own remaining seconds.
 */

/** How long one cue key is considered already-played, in ms. Comfortably
 * shorter than the 1s gap between countdown beeps, long enough to swallow the
 * duplicate that a second mounted copy fires in the same tick. */
const CUE_DEDUP_MS = 400;

const firedAt = new Map<string, number>();

/**
 * Claim a cue for a table. The FIRST caller for a given (gameID, key) inside
 * the dedup window gets `true` and should play; every other mounted copy of
 * that table gets `false`.
 *
 * `key` must identify the specific cue instance — e.g. `warn:3` for the
 * three-second beep of this turn — so that a genuinely new cue is never
 * swallowed as a duplicate.
 */
export function claimCue(gameID: string | number, key: string, nowMs: number): boolean {
  const id = `${String(gameID)}:${key}`;
  const prev = firedAt.get(id);
  if (prev !== undefined && nowMs - prev < CUE_DEDUP_MS) return false;
  firedAt.set(id, nowMs);
  // Bounded cleanup — this map would otherwise grow for the life of the tab.
  if (firedAt.size > 64) {
    firedAt.forEach((t, k) => {
      if (nowMs - t > CUE_DEDUP_MS) firedAt.delete(k);
    });
  }
  return true;
}

/** gameID -> this table's clock-zero moment, which mounted copies of it
 * currently report the player looking at it, and which copies are publishing
 * into the floor at all (ms, same basis as the `nowMs` callers pass in). */
const countdownDeadlines = new Map<
  string,
  { deadline: number; focusedBy: Set<number>; publishedBy: Set<number> }
>();

/** This table is counting down with `secsRemaining` left.
 *
 * Focus is claimed PER MOUNTED COPY (`copyId`), not per table. A table is
 * mounted twice (carousel panel + grid cell) and both copies publish, one
 * focused and one not — a single boolean could not tell the inactive twin's
 * write (which must not clobber the panel's claim) apart from the panel
 * itself reporting the player switched away mid-turn (which MUST release the
 * claim, or the stale flag silences every background table for the rest of
 * the turn while this one loses the race: nobody beeps). So each copy adds or
 * removes only its own claim, and the claims reset with the turn.
 *
 * `publishedBy` records every copy currently publishing, for the same
 * per-copy reason — see clearCountdown. */
export function publishCountdown(
  gameID: string | number,
  secsRemaining: number,
  nowMs: number,
  focused = false,
  copyId = 0
): void {
  const id = String(gameID);
  const deadline = nowMs + secsRemaining * 1000;
  const prev = countdownDeadlines.get(id);
  const sameTurn = prev !== undefined && Math.abs(prev.deadline - deadline) < 1500;
  const focusedBy = sameTurn ? prev.focusedBy : new Set<number>();
  const publishedBy = sameTurn ? prev.publishedBy : new Set<number>();
  if (focused) focusedBy.add(copyId);
  else focusedBy.delete(copyId);
  publishedBy.add(copyId);
  countdownDeadlines.set(id, { deadline, focusedBy, publishedBy });
}

/** This table's turn ended (acted, timed out, or a subtree unmounted).
 *
 * The clear is per-copy for the same reason focus is: BOTH mounted copies of
 * the acting seat publish, and one copy unmounting mid-countdown (a grid
 * re-layout, a table closing) must not wipe the twin's live claim — that
 * reopened the ~1s silent-floor hole, just narrower (dev review #3747 round
 * 2, residual). With a `copyId`, only that copy's claims are released — its
 * publish AND its focus, so a departing focused copy cannot leave a stale
 * focus claim silencing every background table — and the entry dies only when
 * the LAST live publisher stops. Without a `copyId`, this is the force-clear
 * form: the whole entry goes at once (legacy callers and tests keep their
 * semantics). */
export function clearCountdown(gameID: string | number, copyId?: number): void {
  const id = String(gameID);
  if (copyId === undefined) {
    countdownDeadlines.delete(id);
    return;
  }
  const entry = countdownDeadlines.get(id);
  if (entry === undefined) return;
  entry.publishedBy.delete(copyId);
  entry.focusedBy.delete(copyId);
  if (entry.publishedBy.size === 0) countdownDeadlines.delete(id);
}

/**
 * May this table's countdown be heard right now?
 *
 * THE FOCUSED TABLE ALWAYS OWNS ITS OWN COUNTDOWN. The first cut had it
 * compete on deadline like everyone else, and that read as broken in play
 * (owner, 8/27): stare at your own countdown while a background table sits
 * closer to zero and YOUR beats go silent mid-run, then start late on the
 * next turn while a background claim expires — "sometimes 3 beats, sometimes
 * 2, starting anywhere". A countdown you are looking at that skips beats is
 * indistinguishable from a bug, because it is one.
 *
 * So: if the player is looking at a counting table, that table beeps and
 * nothing else does. The deadline race only decides among BACKGROUND tables
 * (closest to zero wins, ties break on gameID, expired claims pruned — the
 * self-healing that makes a stalled table harmless). Still exactly one
 * audible countdown at any moment.
 */
export function holdsCountdownFloor(
  gameID: string | number,
  secsRemaining: number,
  nowMs: number,
  focused = false
): boolean {
  const me = String(gameID);
  const myDeadline = nowMs + secsRemaining * 1000;
  if (focused) return true;
  let holds = true;
  countdownDeadlines.forEach((entry, id) => {
    if (id === me) return;
    if (entry.deadline <= nowMs) {
      countdownDeadlines.delete(id);
      return;
    }
    // A live focused table silences every background countdown outright.
    if (entry.focusedBy.size > 0) {
      holds = false;
      return;
    }
    if (entry.deadline < myDeadline || (entry.deadline === myDeadline && id < me)) holds = false;
  });
  return holds;
}

/** Test-only reset of the module-level registries. */
export function __resetAttentionCues(): void {
  firedAt.clear();
  countdownDeadlines.clear();
}
